import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import RelatedPages from '../components/RelatedPages';
import { getPublicAssetPath } from '../config';

const Documentation: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const { darkMode } = useTheme();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  };

  const tableOfContents = [
    { id: 'getting-started', title: 'Getting Started', icon: 'fa-rocket' },
    { id: 'dashboard', title: 'Dashboard', icon: 'fa-chart-line' },
    { id: 'agreements', title: 'Mengelola Agreement', icon: 'fa-file-contract' },
    { id: 'templates', title: 'Template Management', icon: 'fa-layer-group' },
    { id: 'drafting', title: 'Drafting Workspace', icon: 'fa-edit' },
    { id: 'board', title: 'Agreements Board', icon: 'fa-columns' },
    { id: 'detail', title: 'Agreement Detail', icon: 'fa-file-lines' },
    { id: 'collaboration', title: 'Collaboration & Team', icon: 'fa-users' },
    { id: 'admin', title: 'Admin Panel', icon: 'fa-shield-halved' },
    { id: 'tips', title: 'Tips & Best Practices', icon: 'fa-lightbulb' },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/dashboard" 
            className={`inline-flex items-center text-sm mb-6 transition-colors ${
              darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'
            }`}
          >
            <i className="fa-solid fa-arrow-left mr-2"></i>
            Kembali ke Dashboard
          </Link>
          <div className="flex items-center justify-center mb-6">
            <img src={getPublicAssetPath('/le_long.png')} alt="Legitimu Logo" className="h-12" style={darkMode ? { filter: 'brightness(0) invert(1)' } : {}} />
          </div>
          <h1 className={`text-3xl font-bold text-center mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Documentation</h1>
          <p className={`text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Panduan lengkap penggunaan Legitimu</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Table of Contents - Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className={`rounded-lg border shadow-sm p-6 sticky top-4 transition-colors duration-300 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <i className="fa-solid fa-list text-violet-600 mr-2"></i>
                Daftar Isi
              </h3>
              <nav className="space-y-2">
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors flex items-center ${
                      activeSection === item.id
                        ? 'bg-violet-100 text-violet-700 font-medium'
                        : darkMode
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <i className={`fa-solid ${item.icon} mr-2 text-xs w-4`}></i>
                    <span>{item.title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className={`rounded-lg border shadow-sm p-8 space-y-12 transition-colors duration-300 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              {/* Getting Started */}
              <section id="getting-started" className="scroll-mt-4">
                <h2 className={`text-2xl font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <i className="fa-solid fa-rocket text-violet-600 mr-3"></i>
                  Getting Started
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>1. Login ke Sistem</h3>
                    <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Untuk memulai menggunakan Legitimu, Anda perlu login menggunakan kredensial yang telah diberikan oleh administrator. Akses halaman login melalui URL aplikasi.
                    </p>
                    <div className={`border rounded-lg p-4 transition-colors duration-300 ${
                      darkMode ? 'bg-violet-900/30 border-violet-700' : 'bg-violet-50 border-violet-200'
                    }`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <strong>Catatan:</strong> Jika Anda lupa password, gunakan fitur "Forgot Password" untuk mereset kredensial Anda.
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>2. Dashboard Overview</h3>
                    <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Setelah login berhasil, Anda akan diarahkan ke Dashboard. Dashboard memberikan ringkasan pekerjaan Anda, termasuk:
                    </p>
                    <ul className={`list-disc list-inside space-y-2 mt-3 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li>Tugas yang perlu diselesaikan (My Tasks)</li>
                      <li>Statistik agreement berdasarkan status</li>
                      <li>Notifikasi dan reminder penting</li>
                      <li>Quick access ke fitur-fitur utama</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Dashboard */}
              <section id="dashboard" className="scroll-mt-4">
                <h2 className={`text-2xl font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <i className="fa-solid fa-chart-line text-violet-600 mr-3"></i>
                  Dashboard
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Overview Dashboard</h3>
                    <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Dashboard adalah halaman utama yang menampilkan ringkasan aktivitas dan pekerjaan Anda. Halaman ini memberikan gambaran cepat tentang status agreement, tugas yang perlu diselesaikan, dan statistik penting.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div className={`p-4 border rounded-lg transition-colors duration-300 ${
                        darkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'
                      }`}>
                        <h4 className={`font-semibold mb-2 ${darkMode ? 'text-red-300' : 'text-red-900'}`}>Urgent Tasks</h4>
                        <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>Menampilkan tugas yang mendesak dan perlu segera diselesaikan</p>
                      </div>
                      <div className={`p-4 border rounded-lg transition-colors duration-300 ${
                        darkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
                      }`}>
                        <h4 className={`font-semibold mb-2 ${darkMode ? 'text-yellow-300' : 'text-yellow-900'}`}>Due Soon</h4>
                        <p className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>Tugas yang akan segera jatuh tempo</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Sidebar Navigation</h3>
                    <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Di sidebar kiri, Anda dapat mengakses berbagai fitur seperti Projects/Clients, Views (My Tasks, Team Tasks), dan navigasi cepat ke halaman utama.
                    </p>
                  </div>
                </div>
              </section>

              {/* Agreements */}
              <section id="agreements" className="scroll-mt-4">
                <h2 className={`text-2xl font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <i className="fa-solid fa-file-contract text-violet-600 mr-3"></i>
                  Mengelola Agreement
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Membuat Agreement Baru</h3>
                    <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Untuk membuat agreement baru, ikuti langkah-langkah berikut:
                    </p>
                    <ol className={`list-decimal list-inside space-y-2 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li>Klik tombol "New Request" di Dashboard atau halaman Agreements Board</li>
                      <li>Isi formulir dengan informasi yang diperlukan:
                        <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                          <li>Tipe Agreement (PKS, MOU, PO, BAST, Invoice)</li>
                          <li>Company/Client</li>
                          <li>Counterparty (nama dan alamat pihak lain)</li>
                          <li>Nilai kontrak</li>
                          <li>Tanggal mulai dan berakhir</li>
                          <li>Deskripsi dan scope pekerjaan</li>
                        </ul>
                      </li>
                      <li>Pilih template yang sesuai atau buat dari scratch</li>
                      <li>Review semua informasi yang telah diisi</li>
                      <li>Submit request untuk diproses</li>
                    </ol>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Mengedit Agreement</h3>
                    <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Untuk mengedit agreement yang sedang dalam status DRAFTING, buka detail agreement dan gunakan Drafting Workspace. Di sini Anda dapat mengedit klausul, menambahkan dokumen, dan mengelola tim yang terlibat.
                    </p>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Tracking Status Agreement</h3>
                    <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Setiap agreement memiliki status yang dapat dilacak melalui Agreements Board. Status umum meliputi:
                    </p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                        <span className="text-sm font-medium text-purple-700">NEW</span>
                        <p className="text-xs text-purple-600 mt-1">Agreement baru dibuat</p>
                      </div>
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <span className="text-sm font-medium text-yellow-700">DRAFTING</span>
                        <p className="text-xs text-yellow-600 mt-1">Sedang dalam proses penulisan</p>
                      </div>
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                        <span className="text-sm font-medium text-orange-700">BUSINESS REVIEW</span>
                        <p className="text-xs text-orange-600 mt-1">Menunggu review dari tim bisnis</p>
                      </div>
                      <div className="p-3 bg-teal-50 border border-teal-200 rounded">
                        <span className="text-sm font-medium text-teal-700">LEGAL REVIEW</span>
                        <p className="text-xs text-teal-600 mt-1">Menunggu review dari tim legal</p>
                      </div>
                      <div className="p-3 bg-green-50 border border-green-200 rounded">
                        <span className="text-sm font-medium text-green-700">APPROVED</span>
                        <p className="text-xs text-green-600 mt-1">Sudah disetujui</p>
                      </div>
                      <div className="p-3 bg-indigo-50 border border-indigo-200 rounded">
                        <span className="text-sm font-medium text-indigo-700">RELEASED</span>
                        <p className="text-xs text-indigo-600 mt-1">Sudah dirilis/ditandatangani</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Templates */}
              <section id="templates" className="scroll-mt-4">
                <h2 className={`text-2xl font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <i className="fa-solid fa-layer-group text-violet-600 mr-3"></i>
                  Template Management
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Menggunakan Template</h3>
                    <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Templates memungkinkan Anda untuk membuat agreement dengan cepat menggunakan format yang sudah standar. Fitur ini termasuk:
                    </p>
                    <ul className={`list-disc list-inside space-y-2 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li>Menggunakan template yang sudah ada untuk agreement baru</li>
                      <li>Mencari template berdasarkan kategori atau tipe agreement</li>
                      <li>Melihat preview template sebelum digunakan</li>
                      <li>Mengelola template favorit untuk akses cepat</li>
                      <li>Filter template berdasarkan kategori (Service Agreements, Purchase Orders, BAST, MOU & NDAs, Invoices)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Kategori Template</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <i className="fa-solid fa-handshake text-violet-600 mb-2"></i>
                        <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Service Agreements</h4>
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Template untuk perjanjian jasa</p>
                      </div>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <i className="fa-solid fa-file-invoice text-violet-600 mb-2"></i>
                        <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Purchase Orders</h4>
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Template untuk purchase order</p>
                      </div>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <i className="fa-solid fa-file-alt text-violet-600 mb-2"></i>
                        <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>BAST</h4>
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Berita Acara Serah Terima</p>
                      </div>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <i className="fa-solid fa-file-signature text-violet-600 mb-2"></i>
                        <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>MOU & NDAs</h4>
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Memorandum of Understanding dan NDA</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Drafting Workspace */}
              <section id="drafting" className="scroll-mt-4">
                <h2 className={`text-2xl font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <i className="fa-solid fa-edit text-violet-600 mr-3"></i>
                  Drafting Workspace
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Fitur Drafting Workspace</h3>
                    <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Drafting Workspace adalah area kerja utama untuk mengedit dan menyusun agreement. Fitur-fitur yang tersedia:
                    </p>
                    <ul className={`list-disc list-inside space-y-2 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li><strong>Edit Klausul:</strong> Edit, tambah, atau hapus klausul agreement</li>
                      <li><strong>Document Management:</strong> Upload dan kelola dokumen pendukung</li>
                      <li><strong>Team Collaboration:</strong> Tambahkan anggota tim dan assign role</li>
                      <li><strong>Comments:</strong> Tambahkan komentar pada klausul tertentu</li>
                      <li><strong>Version Control:</strong> Lihat history perubahan dokumen</li>
                      <li><strong>Save Draft:</strong> Simpan progress secara otomatis atau manual</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Mengedit Klausul</h3>
                    <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Di bagian kiri workspace, Anda akan melihat daftar klausul. Klik pada klausul untuk mengeditnya. Anda dapat mengaktifkan atau menonaktifkan klausul tertentu dengan checkbox. Setiap perubahan akan tersimpan secara otomatis.
                    </p>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Upload Dokumen</h3>
                    <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Di bagian Documents, Anda dapat mengupload dokumen pendukung seperti file Word, PDF, atau Excel. Dokumen ini dapat diakses oleh semua anggota tim yang terlibat dalam agreement.
                    </p>
                  </div>
                </div>
              </section>

              {/* Agreements Board */}
              <section id="board" className="scroll-mt-4">
                <h2 className={`text-2xl font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <i className="fa-solid fa-columns text-violet-600 mr-3"></i>
                  Agreements Board
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Kanban Board View</h3>
                    <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Agreements Board menampilkan semua agreement dalam format Kanban board. Setiap kolom mewakili status agreement, memudahkan Anda untuk melihat progress dan mengelola workflow.
                    </p>
                    <div className={`border rounded-lg p-4 transition-colors duration-300 ${
                      darkMode ? 'bg-violet-900/30 border-violet-700' : 'bg-violet-50 border-violet-200'
                    }`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <strong>Tip:</strong> Drag and drop agreement antar kolom untuk mengubah status (jika memiliki permission).
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Filter dan Search</h3>
                    <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Gunakan filter di sidebar untuk melihat agreement berdasarkan company, tipe, atau status tertentu. Search bar memungkinkan Anda mencari agreement berdasarkan nama atau keyword.
                    </p>
                  </div>
                </div>
              </section>

              {/* Agreement Detail */}
              <section id="detail" className="scroll-mt-4">
                <h2 className={`text-2xl font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <i className="fa-solid fa-file-lines text-violet-600 mr-3"></i>
                  Agreement Detail
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Informasi Agreement</h3>
                    <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Halaman detail agreement menampilkan informasi lengkap tentang agreement, termasuk:
                    </p>
                    <ul className={`list-disc list-inside space-y-2 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li>Informasi dasar (nama, tipe, company, counterparty)</li>
                      <li>Status dan timeline</li>
                      <li>Tim yang terlibat</li>
                      <li>History perubahan dan komentar</li>
                      <li>Dokumen terkait</li>
                      <li>Risk assessment (jika tersedia)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Actions yang Tersedia</h3>
                    <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Dari halaman detail, Anda dapat melakukan berbagai aksi seperti mengedit agreement (jika dalam status DRAFTING), mengubah status, menambahkan komentar, atau mengunduh dokumen.
                    </p>
                  </div>
                </div>
              </section>

              {/* Collaboration */}
              <section id="collaboration" className="scroll-mt-4">
                <h2 className={`text-2xl font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <i className="fa-solid fa-users text-violet-600 mr-3"></i>
                  Collaboration & Team
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Menambahkan Team Members</h3>
                    <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Di Drafting Workspace, Anda dapat menambahkan anggota tim yang akan terlibat dalam proses drafting. Setiap anggota dapat diberi role yang berbeda:
                    </p>
                    <ul className={`list-disc list-inside space-y-2 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li><strong>Legal Counsel:</strong> Bertanggung jawab untuk review aspek hukum</li>
                      <li><strong>Business Analyst:</strong> Menganalisis aspek bisnis</li>
                      <li><strong>Project Manager:</strong> Mengkoordinasikan proses</li>
                      <li><strong>Reviewer:</strong> Memberikan feedback dan approval</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Comments & Reviews</h3>
                    <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Gunakan fitur komentar untuk berkomunikasi dengan tim. Anda dapat menambahkan komentar pada klausul tertentu atau pada level dokumen. Semua komentar akan tercatat dengan timestamp dan author.
                    </p>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Notifications</h3>
                    <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Sistem akan mengirimkan notifikasi kepada anggota tim ketika ada perubahan, komentar baru, atau ketika status agreement berubah.
                    </p>
                  </div>
                </div>
              </section>

              {/* Admin Panel */}
              <section id="admin" className="scroll-mt-4">
                <h2 className={`text-2xl font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <i className="fa-solid fa-shield-halved text-violet-600 mr-3"></i>
                  Admin Panel
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Access Management</h3>
                    <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Administrator dapat mengelola akses pengguna melalui tiga sub-menu:
                    </p>
                    <ul className={`list-disc list-inside space-y-2 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li><strong>Users:</strong> Mengelola user accounts, roles, dan status</li>
                      <li><strong>Groups:</strong> Membuat dan mengelola grup pengguna (Legal Team, Business Team, dll)</li>
                      <li><strong>Permissions:</strong> Mengatur permission untuk setiap halaman dan fitur</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Content Management</h3>
                    <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Mengelola konten sistem melalui empat sub-menu:
                    </p>
                    <ul className={`list-disc list-inside space-y-2 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li><strong>Status:</strong> Mengelola status agreement (NEW, DRAFTING, APPROVED, dll)</li>
                      <li><strong>Category:</strong> Mengelola kategori agreement</li>
                      <li><strong>Company:</strong> Mengelola data company/client</li>
                      <li><strong>Agreement Types:</strong> Mengelola tipe agreement (PKS, MOU, PO, dll)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Logs & History</h3>
                    <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Melihat aktivitas sistem, perubahan data, dan history semua aksi yang dilakukan oleh user. Logs dapat difilter berdasarkan tipe aksi (User Actions, System Actions, Permission Changes).
                    </p>
                  </div>
                </div>
              </section>

              {/* Tips */}
              <section id="tips" className="scroll-mt-4">
                <h2 className={`text-2xl font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <i className="fa-solid fa-lightbulb text-violet-600 mr-3"></i>
                  Tips & Best Practices
                </h2>
                <div className="space-y-4">
                  <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <i className="fa-solid fa-magnifying-glass text-violet-600 mr-2"></i>
                      Gunakan Search dan Filter
                    </h4>
                    <p className="text-gray-700 text-sm">
                      Gunakan fitur search dan filter untuk menemukan agreement dengan cepat, terutama jika Anda memiliki banyak agreement aktif. Filter dapat dikombinasikan untuk hasil yang lebih spesifik.
                    </p>
                  </div>
                  <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <i className="fa-solid fa-sync text-violet-600 mr-2"></i>
                      Update Status Secara Berkala
                    </h4>
                    <p className="text-gray-700 text-sm">
                      Selalu review dan update status agreement secara berkala untuk memastikan workflow berjalan lancar. Status yang akurat membantu tim memahami progress dan prioritas.
                    </p>
                  </div>
                  <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <i className="fa-solid fa-layer-group text-violet-600 mr-2"></i>
                      Manfaatkan Template
                    </h4>
                    <p className="text-gray-700 text-sm">
                      Manfaatkan template untuk mempercepat proses drafting dan memastikan konsistensi dokumen. Template yang sudah standar mengurangi risiko kesalahan dan mempercepat review.
                    </p>
                  </div>
                  <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <i className="fa-solid fa-users text-violet-600 mr-2"></i>
                      Kolaborasi dengan Tim
                    </h4>
                    <p className="text-gray-700 text-sm">
                      Tambahkan anggota tim yang relevan sejak awal proses drafting. Komunikasi yang baik melalui komentar membantu menghindari miskomunikasi dan mempercepat proses review.
                    </p>
                  </div>
                  <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <i className="fa-solid fa-save text-violet-600 mr-2"></i>
                      Simpan Progress Secara Berkala
                    </h4>
                    <p className="text-gray-700 text-sm">
                      Meskipun sistem memiliki auto-save, disarankan untuk menyimpan progress secara manual setelah melakukan perubahan penting. Ini memastikan tidak ada data yang hilang.
                    </p>
                  </div>
                </div>
              </section>
            </div>

            {/* Related Pages */}
            <RelatedPages currentPage="documentation" />

            {/* Footer Actions */}
            <div className="mt-8 text-center">
              <Link 
                to="/dashboard" 
                className="inline-flex items-center px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
              >
                <i className="fa-solid fa-arrow-left mr-2"></i>
                Kembali ke Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
